import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterEvent } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { registerLocaleData } from '@angular/common';
import { GlobalConstant } from '@constants/global.constant';
import { TranslateService } from '@ngx-translate/core';
import { ContentService } from '@shared/services/content/content.service';
import { LocalStorageService } from '@shared/services/local-storage/local-storage.service';
import * as fullscreenLoaderActions from '@store/fullscreen-loader/fullscreen-loader.actions';
import { AppState } from '@store/state';
import rg4js from 'raygun4js';
import { TokenExpireConfigService } from './core/interceptors/token-expire-config/token-expire-config.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private unsubscribeAll$: Subject<void> = new Subject();

  constructor(
    @Inject(Store) private _store: Store<AppState>,
    @Inject(Router) private _router: Router,
    @Inject(LocalStorageService) private _localStorageService: LocalStorageService,
    @Inject(GlobalConstant) private _globalConstant: GlobalConstant,
    @Inject(TranslateService) private _translateService: TranslateService,
    @Inject(ContentService) private _contentService: ContentService,
    @Inject(TokenExpireConfigService) private tokenExpireConfigService: TokenExpireConfigService  ) {
  }

  ngOnInit(): void {
    this.subscribeToRouteChange();
    this.setCurrentTranslation();
    this.registerLocales();
    this.detectBFCache();
    this._contentService.getErrorMessages();
    this.tokenExpireConfigService.configTokenExpire();
  }

  private detectBFCache(): void {
    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.location.reload();
      } else {
        rg4js('send', {
          error: new Error('Page loaded normally'),
          customData: {
            url: window.location.href,
            timestamp: new Date().toISOString(),
          },
          tags: ['BFCache', 'pageshow'],
        });
      }
    });
  }

  subscribeToRouteChange(): void {
    this._router.events.pipe(takeUntil(this.unsubscribeAll$)).subscribe((event: RouterEvent) => this.setLoaderVisibility(event));
  }

  setLoaderVisibility(event: RouterEvent) {
    if (event instanceof NavigationStart) {
      this.dispatchLoaderVisibility(true);
    }
    if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
      if (event instanceof NavigationEnd) {
        rg4js('trackEvent', {
          type: 'pageView',
          path: event.url,
        });
      }
      if (event instanceof NavigationError) {
        rg4js('send', {
          error: event.error,
        });
      }
      this.dispatchLoaderVisibility(false);
      this.resetScrollPosition();
    }
  }

  dispatchLoaderVisibility(isVisible: boolean): void {
    this._store.dispatch(fullscreenLoaderActions.setVisibility({ isVisible }));
  }

  resetScrollPosition(): void {
    window.scrollTo({ top: 0 });
  }

  setCurrentTranslation(): void {
    this._translateService.use(this._localStorageService.getCulture());
  }

  registerLocales(): void {
    this._globalConstant.languages.availableLanguages.forEach((lang) => {
      registerLocaleData(lang.locale, lang.value);
    });
  }

  ngOnDestroy(): void {
    this.unsubscribeAll$.next();
    this.unsubscribeAll$.complete();
  }
}
